import React, { useEffect, useState, useRef } from 'react';
import { MapPin, ChevronDown, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarRefreshRegister } from '../contexts/SidebarRefreshContext';
import { useBranchStore } from '../store/branchStore';
import { branchChangeEmitter } from '../lib/branchChangeEvent';

interface Branch {
  id: string;
  name: string;
  location?: string;
  isDefault: boolean;
}

interface BranchSelectorProps {
  onBranchChange?: (branch: Branch) => void;
}

export default function BranchSelector({ onBranchChange }: BranchSelectorProps) {
  const { user } = useAuth();
  const { setRefreshCallback } = useSidebarRefreshRegister();
  const activeBranch = useBranchStore((state) => state.activeBranch);
  const setActiveBranch = useBranchStore((state) => state.setActiveBranch);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [defaultBranch, setDefaultBranch] = useState<Branch | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Setup resize listener for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keep local state in sync with store
  useEffect(() => {
    if (activeBranch?.id) {
      console.log('[BranchSelector] Syncing with store activeBranch:', activeBranch.name);
      setDefaultBranch(activeBranch);
    }
  }, [activeBranch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Fetch user's branches
  const fetchBranches = async () => {
    try {
      const res = await api.get<any>('/branches');
      if (res.branches && Array.isArray(res.branches)) {
        setBranches(res.branches);
        const defBranch = res.branches.find((b: Branch) => b.isDefault);
        setDefaultBranch(defBranch || null);

        // If no active branch in store, set it from API
        if (!activeBranch && defBranch) {
          console.log('[BranchSelector] Setting active branch from API:', defBranch.name);
          setActiveBranch(defBranch);
        }

        // Cache branches in localStorage
        localStorage.setItem('cached_branches', JSON.stringify({
          branches: res.branches,
          defaultBranch: defBranch || null,
          timestamp: Date.now(),
        }));
      }
      setIsLoading(false);
    } catch (error) {
      console.error('[BranchSelector] Failed to fetch branches:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      console.log('[BranchSelector] User ID available, initializing');

      // Check if we already have branches in Zustand store (loaded by useBranchesPreload)
      if (activeBranch && activeBranch.id) {
        console.log('[BranchSelector] Found active branch in store immediately');
        setDefaultBranch(activeBranch as any);
        setIsLoading(false);
        // For now, we just have one branch (the active one) - still need full list
        setBranches([activeBranch as any]);
      }

      // Try loading from cache as fallback
      const cached = localStorage.getItem('cached_branches');
      if (cached && !activeBranch) {
        try {
          console.log('[BranchSelector] Found cached branches, loading as fallback');
          const { branches: cachedBranches, defaultBranch: cachedDefault } = JSON.parse(cached);
          setBranches(cachedBranches);
          setDefaultBranch(cachedDefault);
          setIsLoading(false);
        } catch (e) {
          console.error('[BranchSelector] Failed to parse cached branches:', e);
        }
      }

      // Always fetch fresh data to get full list
      console.log('[BranchSelector] Fetching fresh branches from API');
      fetchBranches();
    }
  }, [user?.id]);

  // Register refresh callback so other components can trigger a refresh
  useEffect(() => {
    setRefreshCallback(fetchBranches);
  }, [setRefreshCallback]);

  const handleBranchSelect = async (branch: Branch) => {
    try {
      // Immediately close dropdown for better UX
      setIsOpen(false);
      // Show loading indicator on the branch selector
      setIsChanging(true);

      console.log('[BranchSelector] Branch change started:', branch.id, branch.name);

      // Update default branch on backend
      const res = await api.put<any>(`/branches/default/${branch.id}`, {});

      if (res.success) {
        console.log('[BranchSelector] Backend confirmed branch change to:', branch.id);
        setDefaultBranch(branch);
        // Update store with the new active branch
        console.log('[BranchSelector] Updating Zustand store with active branch:', branch.id);
        setActiveBranch(branch);

        // Update branches list to reflect new default
        setBranches(branches.map(b => ({
          ...b,
          isDefault: b.id === branch.id
        })));

        // Emit branch change event so all listeners can refresh
        console.log('[BranchSelector] Emitting branch change event');
        branchChangeEmitter.emit(branch);

        if (onBranchChange) {
          onBranchChange(branch);
        }

        console.log('[BranchSelector] Default branch updated:', branch.name);
      }
    } catch (error) {
      console.error('[BranchSelector] Failed to update default branch:', error);
      // Reopen on error so user can retry
      setIsOpen(true);
    } finally {
      setIsChanging(false);
    }
  };

  // Show loading skeleton while fetching
  if (isLoading) {
    return (
      <div className="px-3 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-200 rounded animate-pulse flex-shrink-0" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2 mt-1" />
          </div>
        </div>
      </div>
    );
  }

  // If no branches after loading, don't render
  if (!branches.length) {
    return null;
  }

  // If only one branch, show as read-only
  if (branches.length === 1 && branches[0]) {
    return (
      <div className="px-3 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-cyan-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{branches[0]?.name}</p>
            {branches[0]?.location && (
              <p className="text-xs text-gray-500 truncate">{branches[0].location}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 border-b border-gray-200">
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => !isChanging && setIsOpen(!isOpen)}
          disabled={isChanging}
          className={`w-full grid grid-cols-[1fr_auto] items-center gap-2 text-sm transition-colors ${
            isChanging
              ? 'text-gray-500 cursor-not-allowed'
              : 'text-gray-700 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {isChanging ? (
              <div className="h-4 w-4 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            ) : (
              <MapPin className="h-4 w-4 text-cyan-600 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{defaultBranch?.name || 'Select Branch'}</p>
              {defaultBranch?.location && (
                <p className="text-xs text-gray-500 truncate">{defaultBranch.location}</p>
              )}
            </div>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-400 transition-transform flex-shrink-0',
              isOpen && !isChanging && 'rotate-180'
            )}
          />
        </button>

        {/* Desktop dropdown */}
        {!isMobile && isOpen && !isChanging && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            {branches.map((branch) => (
              <button
                key={branch.id}
                onClick={() => handleBranchSelect(branch)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-cyan-50 transition-colors',
                  branch.isDefault ? 'bg-cyan-50' : ''
                )}
              >
                <div className="flex items-center justify-center w-4 h-4 flex-shrink-0">
                  {branch.isDefault && (
                    <div className="w-2 h-2 bg-cyan-600 rounded-full" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{branch.name}</p>
                  {branch.location && (
                    <p className="text-xs text-gray-500 truncate">{branch.location}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Mobile modal */}
        {isMobile && isOpen && !isChanging && (
          <>
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between rounded-t-xl">
                <h3 className="font-semibold text-gray-900">Select Branch</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="divide-y divide-gray-100">
                {branches.map((branch) => (
                  <button
                    key={branch.id}
                    onClick={() => handleBranchSelect(branch)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-cyan-50 transition-colors',
                      branch.isDefault ? 'bg-cyan-50' : ''
                    )}
                  >
                    <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                      {branch.isDefault && (
                        <div className="w-2.5 h-2.5 bg-cyan-600 rounded-full" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{branch.name}</p>
                      {branch.location && (
                        <p className="text-xs text-gray-500">{branch.location}</p>
                      )}
                    </div>
                    {branch.isDefault && (
                      <span className="text-xs font-medium text-cyan-600 bg-cyan-50 px-2 py-1 rounded">
                        Default
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
